// Service for fetching log data from Cloud Function API

// API base URL - hardcoded for now, would use env vars in production
const API_BASE_URL = 'https://dashboard-api-tf-x5hgxlpcbq-uc.a.run.app';

/**
 * Fetch logs with optional filters
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of logs to retrieve (default 20)
 * @param {number} options.offset - Offset for pagination (default 0)
 * @param {string} options.source - Filter by source (optional)
 * @param {string} options.severity - Filter by severity (optional)
 * @param {string} options.startDate - Filter by start date (optional)
 * @param {string} options.endDate - Filter by end date (optional)
 * @returns {Promise<Object>} Response with logs and pagination info
 */
export const fetchLogs = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    
    // Add parameters if they exist
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    if (options.source) params.append('source', options.source);
    if (options.severity) params.append('severity', options.severity);
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    
    const response = await fetch(`${API_BASE_URL}/logs?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'omit' // Don't send credentials for Cross-Origin requests
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API error response:', errorText);
      throw new Error(`API error: ${response.status} - ${errorText || response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching logs:', error);
    throw error;
  }
};

/**
 * Fetch log summary statistics
 * @param {string} timeRange - Time range for summary (1h, 6h, 24h, 7d, 30d)
 * @returns {Promise<Object>} Response with summary statistics
 */
export const fetchLogSummary = async (timeRange = '24h') => {
  try {
    const response = await fetch(`${API_BASE_URL}/summary?timeRange=${timeRange}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API error response:', errorText);
      throw new Error(`API error: ${response.status} - ${errorText || response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching log summary:', error);
    throw error;
  }
};

/**
 * Fetch logs grouped by source
 * @param {string} timeRange - Time range (1h, 24h, 7d, 30d)
 * @returns {Promise<Object>} Response with logs grouped by source
 */
export const fetchLogsBySource = async (timeRange = '24h') => {
  try {
    const response = await fetch(`${API_BASE_URL}/by-source?timeRange=${timeRange}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'omit'
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API error response:', errorText);
      throw new Error(`API error: ${response.status} - ${errorText || response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching logs by source:', error);
    throw error;
  }
};

export default {
  fetchLogs,
  fetchLogSummary,
  fetchLogsBySource
}; 